import React from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/compat/router';
import { DesktopOnly, MobileOnly } from 'shared/components';
import { withLoginRequired } from 'src/hoc/with-login-required';
import useUser from 'hooks/use-user';
import { useIsMobile } from 'hooks/use-is-mobile';
import { VerticalDivider } from 'components/core';
import { MAIN_CONTENT_ID } from 'components/skip-to-main/constants';

import { Content } from './content';
import { AccountNavBar } from './account-nav-bar';
import { NavBarVariant, ConsumerUser, AccountTab } from './account.types';
import { DposLoyaltyProvider } from './dpos-loyalty-context';

function Account(): JSX.Element {
  const user: ConsumerUser = useUser();
  const { query: { accountTab } = { accountTab: undefined } } = useRouter() ?? { query: {} };
  const isMobile = useIsMobile();

  const pushFooterDown = !(accountTab === AccountTab.orders && isMobile);

  return (
    <BackgroundContainer pushFooterDown={pushFooterDown}>
      <DposLoyaltyProvider>
        <AccountBoxContainer>
          <StyledDesktopOnly mediaQuery='sm'>
            <AccountNavBar user={user} variant={NavBarVariant.side} />
            <VerticalDivider _color='var(--dcm-color-border)' />
            <MainContentContainer role='main' id={MAIN_CONTENT_ID}>
              <Content user={user} />
            </MainContentContainer>
          </StyledDesktopOnly>
          <StyledMobileOnly mediaQuery='xs'>
            <AccountNavBar variant={NavBarVariant.top} />
            <MobileBackgroundContainer>
              <Content user={user} />
            </MobileBackgroundContainer>
          </StyledMobileOnly>
        </AccountBoxContainer>
      </DposLoyaltyProvider>
    </BackgroundContainer>
  );
}

export const AccountPage = withLoginRequired(Account);

const AccountBoxContainer = styled.div`
  display: flex;
  background-color: var(--dcm-account-surface-bg);
  flex: 1 0 100%;
  max-width: 1060px;
  width: 100%;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    margin: 61px 0 122px 0;
    box-shadow: 0 5px 14px var(--dcm-account-surface-shadow);
    border-radius: 14px;
  }
`;

const BackgroundContainer = styled.div<{ pushFooterDown: boolean }>`
  display: flex;
  justify-content: center;
  min-height: ${({ pushFooterDown }) => (pushFooterDown ? '92.5%' : 'unset')};
  width: 100%;
  background-color: var(--dcm-account-bg);
`;

const StyledMobileOnly = styled(MobileOnly)`
  width: 100%;
  flex-direction: column;
`;
const StyledDesktopOnly = styled(DesktopOnly)`
  width: 100%;
`;

const MainContentContainer = styled.div`
  width: 100%;
  padding: 48px 64px;
  min-height: 616px !important;

  @media screen and (max-width: 900px) {
    padding: 24px;
  }
`;

const MobileBackgroundContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  background-color: var(--dcm-account-surface-bg);
  padding: 24px 24px 60px;
`;
